import React, { useEffect, useState } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { useQuery } from '@apollo/client';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, TextField, Typography, withStyles } from '@material-ui/core';
import { getCurrencySymbol } from '../../../../../../utils/stringUtils';

import ModifiersTransferList, { ModifierTransferListItem } from '../../../../../ModifiersTransferList';
import { CategoryReducerAction } from '../..';
import { getDishCategoryModifierAssignmentsQuery, getDishModifiersQuery, getDrinkCategoryModifierAssignmentsQuery, getDrinkModifiersQuery } from './queries';
import {
  deleteDishCategoryModifierAssignment,
  deleteDrinkCategoryModifierAssignment,
  getDishesInCategory,
  getDrinksInCategory,
  insertDishCategoryModifierAssignment,
  insertDishModifier,
  insertDishModifierAssignments,
  insertDrinkCategoryModifierAssignment,
  insertDrinkModifier,
  insertDrinkModifierAssignments,
} from './mutations';

import styles from './styles';

interface Props extends WithStyles<typeof styles> {
  category_type: string;
  classes: ClassNameMap<string>;
  categoryId: string;
  organisationId: string;
  currency: string;
  locale: string;
  dispatch: React.Dispatch<{ type: CategoryReducerAction; value: any }>;
}

interface CategoryModifierAssignment {
  id: string;
  category_id: string;
  modifier_id: string;
  modifier: {
    id: string;
    name: string;
    price: number;
    tax: number;
  };
}

// Extended ModifierTransferListItem with assignment ID
interface ExtendedModifierTransferListItem extends ModifierTransferListItem {
  assignmentId?: string;
}

interface NewModifier {
  name: string;
  price: number;
  tax: number;
}

interface ModifierValidationErrors {
  name: string | false;
  price: string | false;
  tax: string | false;
}

const validation = {
  name: (value: string) => (value.length > 0 ? false : 'Name is required'),
  price: (value: number) => (value >= 0 ? false : 'Price must be positive'),
  tax: (value: number) => (value >= 0 && value <= 1000 ? false : 'Tax must be between 0 and 100%'),
};

const StepModifiers = ({ classes, categoryId, organisationId, currency, locale, dispatch, category_type }: Props): React.ReactElement => {
  const [allModifiers, setAllModifiers] = useState<ExtendedModifierTransferListItem[]>([]);
  const [assignedModifiers, setAssignedModifiers] = useState<ExtendedModifierTransferListItem[]>([]);
  const [selectedModifiers, setSelectedModifiers] = useState<ExtendedModifierTransferListItem[]>([]);
  const [categoryType] = useState<string>(category_type);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showApplyConfirmation, setShowApplyConfirmation] = useState<boolean>(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState<boolean>(false);
  const [isAddModifierDialogOpen, setIsAddModifierDialogOpen] = useState<boolean>(false);
  const [isCreatingModifier, setIsCreatingModifier] = useState<boolean>(false);
  const [newModifier, setNewModifier] = useState<NewModifier>({ name: '', price: 0, tax: 200 });
  const [modifierErrors, setModifierErrors] = useState<ModifierValidationErrors>({ name: false, price: false, tax: false });

  // Queries for modifiers
  const { data: dishModifiersData } = useQuery(getDishModifiersQuery, {
    variables: { organisationId },
    fetchPolicy: 'no-cache',
    skip: categoryType !== 'dish',
  });

  const { data: drinkModifiersData } = useQuery(getDrinkModifiersQuery, {
    variables: { organisationId },
    fetchPolicy: 'no-cache',
    skip: categoryType !== 'drink',
  });

  // Queries for existing category modifier assignments
  const { data: dishCategoryModifiersData, refetch: refetchDishCategoryModifiers } = useQuery(getDishCategoryModifierAssignmentsQuery, {
    variables: { categoryId },
    fetchPolicy: 'no-cache',
    skip: categoryType !== 'dish',
  });

  const { data: drinkCategoryModifiersData, refetch: refetchDrinkCategoryModifiers } = useQuery(getDrinkCategoryModifierAssignmentsQuery, {
    variables: { categoryId },
    fetchPolicy: 'no-cache',
    skip: categoryType !== 'drink',
  });

  // Load available modifiers based on category type and filter for uniqueness
  useEffect(() => {
    if (categoryType === 'dish' && dishModifiersData) {
      const dishModifiers = dishModifiersData.dish_modifiers || [];

      // Filter for unique modifiers based on name, price, and tax
      const uniqueModifiers = filterUniqueModifiers(dishModifiers);

      const mappedModifiers = uniqueModifiers.map((modifier: any) => ({
        id: modifier.id,
        label: modifier.name,
        price: modifier.price,
        tax: modifier.tax,
      }));

      setAllModifiers(mappedModifiers);
    } else if (categoryType === 'drink' && drinkModifiersData) {
      const drinkModifiers = drinkModifiersData.drink_modifiers || [];

      // Filter for unique modifiers based on name, price, and tax
      const uniqueModifiers = filterUniqueModifiers(drinkModifiers);

      const mappedModifiers = uniqueModifiers.map((modifier: any) => ({
        id: modifier.id,
        label: modifier.name,
        price: modifier.price,
        tax: modifier.tax,
      }));

      setAllModifiers(mappedModifiers);
    }
  }, [categoryType, dishModifiersData, drinkModifiersData]);

  // Load existing category modifier assignments
  useEffect(() => {
    if (categoryType === 'dish' && dishCategoryModifiersData) {
      const assignments = dishCategoryModifiersData.dish_category_modifier_assignments || [];
      const mappedAssignments = assignments.map((assignment: CategoryModifierAssignment) => ({
        id: assignment.modifier.id,
        label: assignment.modifier.name,
        price: assignment.modifier.price,
        tax: assignment.modifier.tax,
        assignmentId: assignment.id,
      }));
      setAssignedModifiers(mappedAssignments);
      setSelectedModifiers(mappedAssignments);

      // Map the assigned modifiers to the format expected by the category reducer
      const modifiersForCategory = assignments.map((assignment: CategoryModifierAssignment) => ({
        id: assignment.modifier.id,
        name: assignment.modifier.name,
        price: assignment.modifier.price,
        tax: assignment.modifier.tax,
      }));

      // Dispatch the modifiers to update the category state
      dispatch({ type: CategoryReducerAction.MODIFIERS, value: modifiersForCategory });
    } else if (categoryType === 'drink' && drinkCategoryModifiersData) {
      const assignments = drinkCategoryModifiersData.drink_category_modifier_assignments || [];
      const mappedAssignments = assignments.map((assignment: CategoryModifierAssignment) => ({
        id: assignment.modifier.id,
        label: assignment.modifier.name,
        price: assignment.modifier.price,
        tax: assignment.modifier.tax,
        assignmentId: assignment.id,
      }));
      setAssignedModifiers(mappedAssignments);
      setSelectedModifiers(mappedAssignments);

      // Map the assigned modifiers to the format expected by the category reducer
      const modifiersForCategory = assignments.map((assignment: CategoryModifierAssignment) => ({
        id: assignment.modifier.id,
        name: assignment.modifier.name,
        price: assignment.modifier.price,
        tax: assignment.modifier.tax,
      }));

      // Dispatch the modifiers to update the category state
      dispatch({ type: CategoryReducerAction.MODIFIERS, value: modifiersForCategory });
    }
  }, [categoryType, dishCategoryModifiersData, drinkCategoryModifiersData, dispatch]);

  // Helper function to filter unique modifiers based on name, price, and tax
  const filterUniqueModifiers = (modifiers: any[]) => {
    const uniqueMap = new Map();

    return modifiers.filter((modifier) => {
      // Create a unique key based on name, price, and tax
      const key = `${modifier.name}-${modifier.price}-${modifier.tax}`;

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, true);
        return true;
      }

      return false;
    });
  };

  // Handle modifier selection in the transfer list
  const handleModifierSelection = (left: ModifierTransferListItem[], right: ModifierTransferListItem[]) => {
    // Convert to ExtendedModifierTransferListItem to maintain type safety
    const rightModifiers = right as ExtendedModifierTransferListItem[];
    setSelectedModifiers(rightModifiers);

    // Map the selected modifiers to the format expected by the category reducer
    const modifiersForCategory = rightModifiers.map((modifier) => ({
      id: modifier.id,
      name: modifier.label,
      price: modifier.price,
      tax: modifier.tax,
    }));

    // Dispatch the modifiers to update the category state
    dispatch({ type: CategoryReducerAction.MODIFIERS, value: modifiersForCategory });
  };

  // Handle edit modifier
  const handleEditModifier = (originalModifier: ModifierTransferListItem, updatedModifier: ModifierTransferListItem) => {
    // Update the modifier in the selected modifiers list
    const updatedSelectedModifiers = selectedModifiers.map(mod => 
      mod.id === originalModifier.id 
        ? { ...mod, label: updatedModifier.label, price: updatedModifier.price, tax: updatedModifier.tax }
        : mod
    );
    
    setSelectedModifiers(updatedSelectedModifiers);
    
    // Update the category state
    const modifiersForCategory = updatedSelectedModifiers.map((modifier) => ({
      id: modifier.id,
      name: modifier.label,
      price: modifier.price,
      tax: modifier.tax,
    }));
    
    dispatch({ type: CategoryReducerAction.MODIFIERS, value: modifiersForCategory });
  };

  // Save the selected modifiers to the category
  const saveModifiersToCategory = async () => {
    if (!categoryType) return;

    setIsSaving(true);

    try {
      // Find modifiers to add (in selectedModifiers but not in assignedModifiers)
      const modifiersToAdd = selectedModifiers.filter((item) => !assignedModifiers.some((assigned) => assigned.id === item.id));

      // Find modifiers to remove (in assignedModifiers but not in selectedModifiers)
      const modifiersToRemove = assignedModifiers.filter((assigned) => !selectedModifiers.some((item) => item.id === assigned.id));

      // Add new assignments
      for (const modifier of modifiersToAdd) {
        const assignmentObject = {
          category_id: categoryId,
          modifier_id: modifier.id,
          organisation_id: organisationId,
        };

        if (categoryType === 'dish') {
          await insertDishCategoryModifierAssignment({ object: assignmentObject });
        } else if (categoryType === 'drink') {
          await insertDrinkCategoryModifierAssignment({ object: assignmentObject });
        }
      }

      // Remove assignments
      for (const modifier of modifiersToRemove) {
        if (modifier.assignmentId) {
          if (categoryType === 'dish') {
            await deleteDishCategoryModifierAssignment({ id: modifier.assignmentId });
          } else if (categoryType === 'drink') {
            await deleteDrinkCategoryModifierAssignment({ id: modifier.assignmentId });
          }
        }
      }

      // Refetch the assignments to update the UI
      if (categoryType === 'dish') {
        await refetchDishCategoryModifiers();
      } else if (categoryType === 'drink') {
        await refetchDrinkCategoryModifiers();
      }

      setShowSaveConfirmation(false);
    } catch (error) {
      console.error('Error saving modifiers to category:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Apply modifiers to all items in the category
  const applyModifiersToAllItems = async () => {
    if (!categoryType || assignedModifiers.length === 0) return;

    setIsApplying(true);
    try {
      // Get all items in the category
      let items: Array<{ id: string; organisation_id: string }> = [];

      if (categoryType === 'dish') {
        const response = await getDishesInCategory({ categoryId });
        items = response.data.dishes;
      } else if (categoryType === 'drink') {
        const response = await getDrinksInCategory({ categoryId });
        items = response.data.drinks;
      }

      // Create modifier assignments for each item
      const modifierAssignments = [];

      for (const item of items) {
        for (const modifier of assignedModifiers) {
          modifierAssignments.push({
            [categoryType === 'dish' ? 'dish_id' : 'drink_id']: item.id,
            modifier: {
              data: {
                name: modifier.label,
                price: modifier.price,
                tax: modifier.tax,
                organisation_id: organisationId,
              },
            },
            organisation_id: organisationId,
          });
        }
      }

      // Insert the modifier assignments
      if (modifierAssignments.length > 0) {
        if (categoryType === 'dish') {
          await insertDishModifierAssignments({ objects: modifierAssignments });
        } else if (categoryType === 'drink') {
          await insertDrinkModifierAssignments({ objects: modifierAssignments });
        }
      }

      setShowApplyConfirmation(false);
    } catch (error) {
      console.error('Error applying modifiers to all items:', error);
    } finally {
      setIsApplying(false);
    }
  };

  // Handle new modifier form changes
  const handleModifierChange = (field: keyof NewModifier, value: string) => {
    let processedValue: string | number = value;
    if (field === 'price') {
      processedValue = Math.round(+value * 100);
    } else if (field === 'tax') {
      processedValue = Math.round(+value * 10);
    }
    setNewModifier(prev => ({ ...prev, [field]: processedValue }));
  };

  // Validate modifier field
  const validateModifierField = (field: keyof NewModifier) => {
    const errorItems: Record<string, any> = {};
    switch (field) {
      case 'name':
        errorItems.name = validation.name(newModifier.name);
        break;
      case 'price':
        errorItems.price = validation.price(newModifier.price);
        break;
      case 'tax':
        errorItems.tax = validation.tax(newModifier.tax);
        break;
      default:
        break;
    }
    setModifierErrors(prev => ({ ...prev, ...errorItems }));
    return Object.values(errorItems).some((error) => error !== false);
  };

  // Handle creating a new modifier
  const handleCreateModifier = async () => {
    const hasNameError = validateModifierField('name');
    const hasPriceError = validateModifierField('price');
    const hasTaxError = validateModifierField('tax');

    if (hasNameError || hasPriceError || hasTaxError) {
      return;
    }

    setIsCreatingModifier(true);
    try {
      const modifierObject = {
        name: newModifier.name,
        price: newModifier.price,
        tax: newModifier.tax,
        organisation_id: organisationId,
      };

      let createdModifier;
      if (categoryType === 'dish') {
        const result = await insertDishModifier({ object: modifierObject });
        createdModifier = result.data?.insert_dish_modifiers_one;
      } else if (categoryType === 'drink') {
        const result = await insertDrinkModifier({ object: modifierObject });
        createdModifier = result.data?.insert_drink_modifiers_one;
      }

      if (createdModifier) {
        // Add the new modifier to the available modifiers list
        const newModifierItem: ExtendedModifierTransferListItem = {
          id: createdModifier.id,
          label: createdModifier.name,
          price: createdModifier.price,
          tax: createdModifier.tax,
        };
        setAllModifiers(prev => [...prev, newModifierItem]);
      }

      // Reset form and close dialog
      setNewModifier({ name: '', price: 0, tax: 200 });
      setModifierErrors({ name: false, price: false, tax: false });
      setIsAddModifierDialogOpen(false);
    } catch (error) {
      console.error('Error creating modifier:', error);
    } finally {
      setIsCreatingModifier(false);
    }
  };

  return (
    <div className={classes.root}>
      <ModifiersTransferList
        labelLeft="Available modifiers"
        labelRight="Selected modifiers"
        description={`Select modifiers to assign to this ${categoryType || ''} category`}
        allItems={allModifiers}
        currentItems={assignedModifiers}
        handleTransfer={handleModifierSelection}
        currency={currency}
        locale={locale}
        onEditModifier={handleEditModifier}
      />

      <div className={classes.actions}>
        <Button variant="outlined" color="primary" onClick={() => setIsAddModifierDialogOpen(true)}>
          Create New Modifier
        </Button>
      </div>

      <div className={classes.applyToAllContainer}>
        <Typography variant="h6" className={classes.applyToAllTitle}>
          Apply to Category Items
        </Typography>
        <Typography variant="body2" className={classes.applyToAllDescription}>
          Selected modifiers will be applied to all items in this category.
        </Typography>
        {/* 
        Button commented out as this functionality is now integrated into the main category save function
        <Button variant="contained" color="primary" onClick={() => setShowApplyConfirmation(true)} disabled={assignedModifiers.length === 0 || !categoryType}>
          Apply Modifiers to All Items
        </Button>
        */}
      </div>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveConfirmation} onClose={() => setShowSaveConfirmation(false)}>
        <DialogTitle>Save Modifiers to Category</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body1" gutterBottom>
            This will save the selected modifiers to the category. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSaveConfirmation(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={saveModifiersToCategory} color="primary" variant="contained" disabled={isSaving}>
            {isSaving ? (
              <>
                Saving...
                <CircularProgress size={24} className={classes.buttonProgress} />
              </>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Confirmation Dialog */}
      <Dialog open={showApplyConfirmation} onClose={() => setShowApplyConfirmation(false)}>
        <DialogTitle>Apply Modifiers to All Items</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body1" gutterBottom>
            This will add the selected modifiers to all items in this category. Are you sure you want to continue?
          </Typography>
          <Typography variant="body2" color="error" gutterBottom>
            Note: This action cannot be undone automatically. You would need to manually remove these modifiers from each item if needed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApplyConfirmation(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={applyModifiersToAllItems} color="primary" variant="contained" disabled={isApplying}>
            {isApplying ? (
              <>
                Applying...
                <CircularProgress size={24} className={classes.buttonProgress} />
              </>
            ) : (
              'Apply'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create New Modifier Dialog */}
      <Dialog open={isAddModifierDialogOpen} onClose={() => setIsAddModifierDialogOpen(false)} aria-labelledby="create-modifier-dialog-title">
        <DialogTitle id="create-modifier-dialog-title">Create New Modifier</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            type="text"
            fullWidth
            value={newModifier.name}
            onChange={(e) => handleModifierChange('name', e.target.value)}
            onBlur={() => validateModifierField('name')}
            error={!!modifierErrors.name}
            helperText={modifierErrors.name || ''}
          />
          <TextField
            margin="dense"
            label="Price"
            type="number"
            fullWidth
            value={newModifier.price / 100 || ''}
            onChange={(e) => handleModifierChange('price', e.target.value)}
            onBlur={() => validateModifierField('price')}
            error={!!modifierErrors.price}
            helperText={modifierErrors.price || ''}
            inputProps={{
              min: 0,
              step: 0.01,
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency, locale)}</InputAdornment>,
            }}
          />
          <TextField
            margin="dense"
            label="Tax"
            type="number"
            fullWidth
            value={newModifier.tax / 10 || ''}
            onChange={(e) => handleModifierChange('tax', e.target.value)}
            onBlur={() => validateModifierField('tax')}
            error={!!modifierErrors.tax}
            helperText={modifierErrors.tax || ''}
            inputProps={{
              min: 0,
              max: 100,
              step: 0.1,
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">%</InputAdornment>,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddModifierDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCreateModifier} color="primary" variant="contained" disabled={isCreatingModifier}>
            {isCreatingModifier ? (
              <>
                Creating...
                <CircularProgress size={24} className={classes.buttonProgress} />
              </>
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default withStyles(styles)(StepModifiers);
